import { SharingNetwork } from "sharethis-reactjs";

export interface urlParams {
  position?: urlParamsPosition;
}

export enum UseFrom {
  none = "none",
  campaign = "campaign",
  offer = "offer",
}

export enum RedeemBy {
  webemail = "webemail",
  email = "email",
}

export enum DelayType {
  none = "none",
  minutes = "minutes",
  hours = "hours",
  days = "days",
  months = "months",
}

export enum ImageRepresentation {
  top = "top",
  left = "left",
  right = "right",
}

export enum LayoutDevices {
  desktop = "desktop",
  mobile = "mobile",
}

export enum Views {
  initial = "initial",
  main = "main",
}

export enum LayoutChildClass {
  button = "button",
  popup = "popup",
}

export enum urlParamsPosition {
  bottom = "bottom",
  left = "left",
  right = "right",
}

export enum DeviceType {
  desktop = "desktop",
  mobile = "mobile",
}

export enum ImageSize {
  all = "all",
  big = "300x300",
  medium = "200x200",
  small = "100x100",
}

export enum PlacementStage {
  pre = "pre",
  post = "post",
}

export enum LayoutType {
  embedded = "embedded",
  popup = "popup",
}

export enum CONDITION_OPERATORS_TEXT {
  in = "In",
  notIn = "Not In",
  equal = "Equal",
  notEqual = "Not Equal",
  contains = "Contains",
  notContains = "Not Contains",
}

export enum CONDITION_OPERATORS_AMOUNT {
  equal = "Equal",
  notEqual = "Not Equal",
  greaterThan = "Greater Than",
  greaterThanorEqual = "Greater Than Or Equal",
  lessThan = "Less Than",
  lessThanorEqual = "Less Than Or Equal",
}

export enum CONDITION_SEARCH_TYPE {
  text = "Text",
  amount = "Amount",
}

export enum CONDITION_SEARCH_LOCATION {
  none = "None",
  pageTitle = "Page Title",
  other = "Other",
}

export enum CONDITION_LOGICAL_OPERATORS {
  and = "and",
  or = "or",
}

export enum TemplateIcons {
  default = "default",
  custom = "custom",
}

export enum AnimationType {
  spin = "spin",
  bounce = "bounce",
  shake = "shake",
  pulse = "pulse",
  flash = "flash",
  tada = "tada",
}

export enum TemplateSelectType {
  checkbox = "checkbox",
  switch = "switch",
  button = "button",
}

export interface QueryParams {
  pageParams: {
    page: string;
  };
  contactDetails?: QuerySaveEmail[];
  selectedOffers?: string[];
  companyToken: string;
}

/** FE logger feature tags: dl = datalayer, tmpl = template/load issues */
export type TrackingLoggerFeature = "dl" | "tmpl" | string;

export interface TrackingLoggerParams {
  message: string;
  companyId: string;
  placementId: string;
  campaignId: string;
  templateId: string;
  layoutId: string;
  page: string;
  feature?: TrackingLoggerFeature;
}

export interface QuerySaveEmail {
  offerid: string;
  email: string;
  offerLink: string;
  companyId: string;
  campaignId: string;
  templateId?: string;
  placementId: string;
  slot: number;
  pltype: PlacementStage;
  orderId: string;
  delayMinutes?: number;
  layoutId: string;
}
export interface ContactDetails {
  email: string;
  offerId: string;
  offerLink: string;
  placementId: string;
  placementName: string;
  layoutId: string;
}

export interface QueryBundle {
  offerId: string;
  campaignId: string;
  placementId: string;
  orderId: string;
  userSessionId: string;
  imageUrl: string;
  pageUrl: string;
  templateId: string;
  placementName: string;
  pltype: PlacementStage;
  slot: number;
  layoutId: string;
}

export interface QueryPostEmail {
  campaignId: string;
  campaignName: string;
  companyId: string;
  contactDetails: ContactDetails[];
  isAuto: boolean;
  offerIds: string[];
  placementId: string;
  placementName: string;
  templateId?: string;
  pltype: PlacementStage;
  layoutId: string;
}

export interface QueryPostOfferView {
  offerIds?: string[];
  offers?: OffersIdSlots[];
  campaignId: string;
  campaignName: string;
  companyId: string;
  placementId: string;
  placementName: string;
  pageUrl: string;
  templateId: string;
  pltype: PlacementStage;
  layoutId: string;
}

export interface TrackingShare {
  offerid: string;
  offerLink: string;
  companyId: string;
  campaignId: string;
  campaignName: string;
  templateId: string;
  slot: number;
  placementId: string;
  placementName: string;
  pageUrl: string;
  shareType: string;
  orderId: string;
  layoutId: string;
}

export interface TrackingParams {
  event: string;
  red?: string;
  campaignId?: string;
  offerId?: string;
  retailerId?: string;
  advertiserId?: string;
  placementId?: string;
  placementName?: string;
  tid?: string;
  slot?: number;
  pltype: PlacementStage;
  pltid: string;
}

export interface CampaignSharingOptions {
  allowSharing: boolean;
  shareAdvertiserOffer: boolean;
  shareMessage: string;
  sharingPlatforms: {
    platforms: SharingNetwork[];
  };
}

export interface CampaignCookieOptions {
  allowCookies: boolean;
  allowSessionCookies: boolean;
  allowPersistentCookies: boolean;
  expiryDays: string;
}

export interface CampaignType {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  company_Id: string;
  startDate: string;
  endDate: string;
  campaignGeneralOptions: CampaignOptions;
  completionStatus: {
    status: boolean;
    next: CAMPAIGNSTEPS;
  };
  allowSharing: boolean;
  templateId: string;
  offerCount: number;
  templateSettings: LayoutSettings;
  sharingOptions: CampaignSharingOptions;
  placementSettings: PlacementSettings[];
  cookieOptions: CampaignCookieOptions;
  offers: Offer[];
  isOpen?: boolean;
}

export interface CampaignOptions {
  automaticallyCheckOptIn: boolean;
  emailCaptureOnAddToOrder: boolean;
  emailCaptureOnOffers: boolean;
  manualEmailEntry?: boolean;
  autoSendEmail?: boolean;
  saveOfferOptions: {
    allowEmail: boolean;
    allowSavingOffers: boolean;
    allowSms: boolean;
  };
  sendOneEmailPerOffer: boolean;
  showAddToOrder: boolean;
  storeEmailForFuture: boolean;
  useEmailFromDataLayer: boolean;
}

export interface LayoutSettings {
  mobile: LayoutDeviceSettings;
  desktop: LayoutDeviceSettings;
}

export interface ButtonShowMore {
  textColor: string;
  backgroundColor: string;
  borderColor: string;
  fontWeight: number;
  rounding: number;
  input: string;
}

export interface TemplateFilters {
  deviceType: LayoutDevices;
  imageSize: ImageSize;
  stage: PlacementStage;
  layoutType: LayoutType;
  offerCount?: string;
}

export interface Template extends TemplateFilters {
  isActive: boolean;
  name: string;
  identifier: string;
  imageURL: string;
}

export interface LayoutDeviceSettings {
  type?: LayoutClass;
  button: ButtonSettings;
  popup: PopupSettings;
  action: ActionSettings;
  inline: InlineSettings;
  template: Template;
  settings?: TemplateOptionSettings;
}

export type FilterStyle = "horizontal" | "short";
export type FilterAlign = "left" | "center" | "right";

export enum FilterType {
  row = "row",
  popup = "popup",
}

export interface FiltersSettings {
  filters: boolean;
  filterType: FilterType;
  textColor: string;
  arrowColor?: string;
  activeColor: string;
  borderUnderline: boolean;
  backgroundColor: string;
  backgroundActiveColor: string;
  borderColor: string;
  borderActiveColor: string;
  showMorePersonalOffers: string;
  showMoreTradeOffers: string;
  filterOffers: string;
  personalOffers: string;
  tradeOffers: string;
  allOffers: string;
  filterStyle?: FilterStyle;
  filterAlign?: FilterAlign;
}

export interface GroupingSettings {
  trending: boolean;
  trendingCount: number;
  trendingText: string;
}

export const groupingDefaultSettings: GroupingSettings = {
  trending: true,
  trendingCount: 5,
  trendingText: "Trending",
};

export interface RedirectionPlacement {
  id: string;
  page: string;
}
export interface LayoutOptions {
  showEmail: boolean;
  showShare: boolean;
  showBadge: boolean;
  redirection: boolean;
  redirectionButtonText: string;
  redirectionButtonTextColor: string;
  redirectionButtonBackground: string;
  redirectionButtonBorderColor: string;
  redirectionButtonBorderRadius: number;
  redirectionPlacement: RedirectionPlacement;
}
export interface TemplateOptionSettings {
  location: LocationSettings;
  embeddedElement?: ElementSettings;
  mainLayout: MainLayoutSettings;
  dealLayout: DealLayoutSettings;
  showMore?: ButtonShowMore;
  filters?: FiltersSettings;
  otherSettings?: OtherSettingsInterface;
  layoutOptions?: LayoutOptions;
  sections?: SectionSettings;
}

export interface SectionSettings {
  topMenu: SectionTopMenuSettings;
  sections: SingleSectionSettings[];
}

export interface SingleSectionSettings {
  title: string;
  description: string;
  titleColor: string;
  descriptionColor: string;
  columns: number | "auto";
  numberOfOffers: number;
  offerCardSize: "normal" | "large";
  showLogo: boolean;
  offerCategoryId: string[];
  featuredOffers: SectionOffer[];
  offers: SectionOffer[];
  includeTopMenu: boolean;
  distributeOffers: boolean;
}

export interface SectionOffer {
  id: string;
  slot: number;
}

export const SECTION_TITLE_PLACEHOLDER = "Section Title";
export const SECTION_DESCRIPTION_PLACEHOLDER = "Section description";
export const SECTION_TITLE_DEFAULT_COLOR = "#9aa0a6";
export const SECTION_DESCRIPTION_DEFAULT_COLOR = "#9aa0a6";
export const SECTION_AUTO_NUMBER_OF_OFFERS_DEFAULT = 4;
export const SECTION_OFFER_CARD_SIZE_DEFAULT: "normal" | "large" = "normal";
export const resolveSectionOfferCategoryId = (
  section?: Partial<SingleSectionSettings>
): string[] => {
  const value = section?.offerCategoryId as unknown;
  if (Array.isArray(value)) {
    return value.filter(
      (id): id is string => typeof id === "string" && id.length > 0
    );
  }
  if (typeof value === "string" && value.length > 0) {
    return [value];
  }
  return [];
};

export const resolveSectionOfferCardSize = (
  section?: Partial<SingleSectionSettings>
): "normal" | "large" =>
  section?.offerCardSize === "large" ? "large" : SECTION_OFFER_CARD_SIZE_DEFAULT;

export const resolveSectionShowLogo = (
  section?: Partial<SingleSectionSettings>
): boolean => section?.showLogo !== false;

export const resolveSectionNumberOfOffers = (
  section?: Partial<SingleSectionSettings>
): number => {
  const n = Number(section?.numberOfOffers);
  return Number.isFinite(n) && n > 0
    ? Math.floor(n)
    : SECTION_AUTO_NUMBER_OF_OFFERS_DEFAULT;
};

export interface SectionTopMenuSettings {
  enabled: boolean;
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  borderRadius: number;
  activeBackgroundColor: string;
  activeTextColor: string;
  activeBorderColor: string;
}

export const singleSectionSettingsDefault: SingleSectionSettings = {
  title: SECTION_TITLE_PLACEHOLDER,
  description: SECTION_DESCRIPTION_PLACEHOLDER,
  titleColor: SECTION_TITLE_DEFAULT_COLOR,
  descriptionColor: SECTION_DESCRIPTION_DEFAULT_COLOR,
  columns: 3,
  numberOfOffers: SECTION_AUTO_NUMBER_OF_OFFERS_DEFAULT,
  offerCardSize: SECTION_OFFER_CARD_SIZE_DEFAULT,
  showLogo: true,
  offerCategoryId: [],
  featuredOffers: [],
  offers: [],
  includeTopMenu: true,
  distributeOffers: false,
};

export const sectionTopMenuDefault: SectionTopMenuSettings = {
  enabled: true,
  backgroundColor: "#f5f6f7",
  textColor: "#3b4047",
  borderColor: "#3ab78f",
  borderRadius: 24,
  activeBackgroundColor: "#3ab78f",
  activeTextColor: "#ffffff",
  activeBorderColor: "#3ab78f",
};

export const sectionSettingsDefault: SectionSettings = {
  topMenu: { ...sectionTopMenuDefault },
  sections: [{ ...singleSectionSettingsDefault }],
};

export const normalizeSingleSection = (
  section?: Partial<SingleSectionSettings>
): SingleSectionSettings => ({
  ...singleSectionSettingsDefault,
  ...section,
  title:
    typeof section?.title === "string"
      ? section.title
      : SECTION_TITLE_PLACEHOLDER,
  description:
    typeof section?.description === "string"
      ? section.description
      : SECTION_DESCRIPTION_PLACEHOLDER,
  titleColor: section?.titleColor || SECTION_TITLE_DEFAULT_COLOR,
  descriptionColor:
    section?.descriptionColor || SECTION_DESCRIPTION_DEFAULT_COLOR,
  featuredOffers: section?.featuredOffers || [],
  offers: section?.offers || [],
  numberOfOffers: resolveSectionNumberOfOffers(section),
  offerCardSize: resolveSectionOfferCardSize(section),
  showLogo: resolveSectionShowLogo(section),
  offerCategoryId: resolveSectionOfferCategoryId(section),
  distributeOffers: Boolean(section?.distributeOffers),
});

export const normalizeSectionSettings = (
  value?: SectionSettings | SingleSectionSettings[]
): SectionSettings => {
  if (Array.isArray(value)) {
    return {
      topMenu: { ...sectionTopMenuDefault },
      sections: (value.length ? value : [{ ...singleSectionSettingsDefault }]).map(
        normalizeSingleSection
      ),
    };
  }

  return {
    topMenu: {
      ...sectionTopMenuDefault,
      ...value?.topMenu,
    },
    sections: (
      value?.sections?.length
        ? value.sections
        : [{ ...singleSectionSettingsDefault }]
    ).map(normalizeSingleSection),
  };
};

export interface MainLayoutSettings {
  fontFamily: string;
  fontFamilyCss: string;
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  borderRadius: number;
  align: TextAlign;
  offersNumber: string;
  rowsNumber: string;
  startAsExpanded: boolean;
  paddingTopBottom: string;
  paddingLeftRight: string;
  highlightColor: string;
  showFeatured: boolean;
  showFeaturedVideo?: boolean;
  featuredSize: "small" | "large";
  featurePosition: "top" | "bottom" | "middle";
  headerHTML: string;
  footerHTML: string;
  headerText: string;
  headerIcon: "lock" | "gift" | "none" | "custom";
  headerCustomIcon?: string;
  headerFontSize: string;
  headerFontWeight: number;
  headerTextColor: string;
  subHeaderText: string;
  subHeaderFontSize: string;
  subHeaderFontWeight: number;
  subHeaderTextColor: string;
  footerText: string;
  headerEmailAutomaticTermsText?: string;
  headerEmailAutomaticTermsTextCompact?: string;
  headerEmailAutomaticTermsTextEmailLink?: string;
  showQualifiedHeading: boolean;
  qualifiedHeadingText: string;
  fixedSizeWithColumns: boolean;
  iconAnimation: boolean;
  iconAnimationType: AnimationType;
  iconAnimationRepeat: number;
  grouping?: GroupingSettings;
}

export interface DealLayoutIcon {
  key: string;
  value: string;
}

export interface DealLayoutSettings {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  fontSize: string;
  align: TextAlign;
  icons: TemplateIcons;
  iconInfo: DealLayoutIcon;
  iconEmail: DealLayoutIcon;
  iconShare: DealLayoutIcon;
  buttonBackgroundColor: string;
  buttonBorderColor?: string;
  buttonTextColor: string;
  buttonFontSize: string;
  buttonFontWeight: number;
  buttonRounding: number;
  buttonTextTransform: TextTransform;
  buttonAlign?: string;
  buttonWidthType?: string;
  buttonWidthValue?: number;
  emailOpen: boolean;
  emailMeTheGift: string;
  emailSend: string;
  enterEmail: string;
  emailTextColor: string;
  emailIconColor: string;
  emailBorderColor?: string;
  emailRounding?: number;
  emailBackground?: string;
  emailFontSize?: string;
  emailFontWeight?: number;
  emailButtonBackgroundColor: string;
  emailButtonTextColor: string;
  emailButtonBorderColor?: string;
  emailButtonRounding: number;
  emailDropdownBackground?: string;
  showHeader?: string;
}

export interface OtherSettingsInterface {
  selectedButtonText: string;
  notSelectedButtonText: string;
  showSelectAll: boolean;
  selectedText: string;
  showCounter: boolean;
  counterText: string;
  showCheckboxSelect: boolean;
  selectType: TemplateSelectType;
  fontWeight: number;
  fontSize: string;
  borderRadius: number;
  backgroundColor: string;
  activeBackgroundColor: string;
  textColor: string;
  activeTextColor: string;
  borderColor: string;
  activeBorderColor: string;
}

export interface OfferCategory {
  category_id: string;
  name: string;
}

export interface CompanyCategory {
  id: string;
  name: string;
}

export interface Country {
  id: string;
  currencySymbol: string;
  isoCode: string;
  name: string;
}

export interface Retailer {
  id: string;
  name: string;
  logoUrl: string;
}

export interface FrequncyCapping {
  isEnabled: boolean;
  frequency: number | null;
  reset: number | null;
}

export interface FrequncyCappingOffer {
  useFrom: UseFrom;
  frequency: number;
  reset: number;
}

export interface OfferRule {
  searchType: CONDITION_SEARCH_TYPE;
  operator: CONDITION_OPERATORS_AMOUNT | CONDITION_OPERATORS_TEXT;
  targetValue: string;
  location: CONDITION_SEARCH_LOCATION;
  locationValue: string;
}

export interface OfferRules {
  logicalOperator: string;
  rules: OfferRule[];
}

export interface OfferRelevance {
  showIfFalse: boolean;
  showMode: number;
  shortBlockedMessage: string;
  longBlockMessage: string;
  successUnlockMessage: string;
  slot: string;
  rules: OfferRules[];
}

export interface OfferAppearance {
  frequencyCapping: FrequncyCappingOffer;
  relevance: OfferRelevance;
}

export enum VoucherType {
  SINGLE = "single",
  CUSTOM = "custom",
}

export enum MediaType {
  main = "main",
  email = "email",
  featured = "featured",
}

export interface ImageUpload {
  filename: string;
  folderPath: string;
  filepath: string;
}

export interface OfferFeatureMedia {
  featuredVideoURL: string;
  images: ImageUpload[];
  title: string;
  description: string;
}

export interface OfferResolutionMedia {
  type: string;
  size: string;
  images: ImageUpload[];
  title: string;
  description: string;
}

export interface TagBlocked {
  difference: number;
  differencePercent: number;
  externalValue: string | null;
  isAmountRule: boolean;
  isBlocked: boolean;
  longBlockMessage: string | null;
  shortBlockedMessage: string | null;
  successUnlockMessage: string | null;
  targetValue: string | null;
}

export interface Offer {
  isAdded?: boolean;
  status: string;
  displayStatus: string;
  type: string;
  title: string;
  description: string;
  longDescription: string;
  faqs: string;
  terms: string;
  imageUrl: string;
  startDate: string;
  endDate: string;
  destinationUrl: string;
  trackingUrl: string;
  offerCTAText: string;
  tagBlocked: TagBlocked;
  offerCpa: {
    display: string;
    value: number;
  };
  epc: number;
  voucher: {
    code: string;
    exclusive: boolean;
    voucherType: VoucherType;
  };
  exclusiveTo: {
    categories: CompanyCategory[];
    regions: Country[];
    retailers: Retailer[];
  };
  appearanceRules: OfferAppearance;
  categories: OfferCategory[];
  brandswopExclusive: boolean;
  isExternal: boolean;
  externalNetwork: {
    promotionId: number;
  };
  advertiser?: {
    name: string;
    company_Id: string;
    external_aId: string;
    uniqueNetworkIdentifier: string;
  };
  advertiserName?: string;
  advertiserLogo?: string;
  slot: number;
  media: {
    featured: OfferFeatureMedia;
    mainImage: ImageUpload[];
    videoURL: string;
    resolution: OfferResolutionMedia[];
  };

  offerSettings: {
    recPlacement: string[];
    allowShare: boolean;
    allowStacked: boolean;
    redeemBy: RedeemBy;
    delaySetting: {
      delayType: DelayType;
      delayValue: string;
      emailTemplateId: string;
    };
    isB2C: boolean;
    bundleOptions: {
      isBundled: boolean;
      originOffer: {
        offerId: string;
        advertiser: string;
        imageURL: string;
        description: string;
        title: string;
        endDate: string;
      };
      destinationOffer: {
        offerId: string;
        advertiser: string;
        imageURL: string;
        description: string;
        title: string;
        endDate: string;
      };
    };
  };
  id: string;
  orderId: string;
  badge: {
    id: string;
    name: string;
    useBorder: boolean;
    backgroundColor: string;
    fontColor: string;
    isDefault: boolean;
  } | null;
}

export interface BundleOffer {
  id: string;
  bundleOfferId: string;
  title: string;
  description: string;
  longDescription: string;
  imageUrl: string;
  faqs: string;
  terms: string;
  offerCTAText: string;
  trackingURL: string;
  originOfferId: string;
  destinationOfferId: string;
  userSession: string;
  campaignId: string;
  companyId: string;
  placementId: string;
  orderId: string;
}

export interface ButtonOptions {
  location: LocationPosition;
  text: string;
  backgroundColor: string;
  textColor: string;
  fontFamily: string;
  fontWeight: number;
  fontSize: string;
  borderRadius: number;
}

export enum CAMPAIGNSTEPS {
  details = "details",
  offers = "offers",
  layout = "layout",
  placement = "placement",
}

export interface PlacementSettings {
  id: string;
  page: string;
  isRedeemable: boolean;
  hasExitView: boolean;
  isVisible: boolean;
  displayOnly: boolean;
  promoMessage: string;
  name: string;
  isEdit: boolean;
  templateSettings: LayoutSettings;
}

export interface GeneralOptions {
  location: LocationPosition;
  offers: string;
  align: TextAlign;
  headerTextColor: string;
  text: string;
  backgroundColor: string;
}

export interface ButtonSettings {
  location: ButtonLocationSettingsT;
  text: string;
  sizes: ButtonSizesSettings;
  shape: ButtonShapes;
  style: ButtonStyleSettingsT;
}

export interface PopupSettings {
  minimized: boolean;
  general: GeneralSettings;
  location: LocationSettings;
  header: TextSettings;
  text: TextSettings;
  image: ImageSettings;
  getButton: GetButtonSettings;
  imageRepresentation: ImageRepresentationSettings;
}

export interface ActionSettings {
  minimized: boolean;
  general: GeneralSettings;
  location: LocationSettings;
  behaviour: BehaviourSettings;
  header: TextSettings;
  text: TextSettings;
  image: ImageSettings;
  getButton: GetButtonSettings;
  imageRepresentation: ImageRepresentationSettings;
}

export interface InlineSettings {
  minimized: boolean;
  element: ElementSettings;
  general: GeneralSettings;
  behaviour: BehaviourSettings;
  header: TextSettings;
  text: TextSettings;
  image: ImageSettings;
  getButton: GetButtonSettings;
  imageRepresentation: ImageRepresentationSettings;
}

export interface ElementSettings {
  attribute: string;
  existingElement: boolean;
  elementType: "id" | "class";
  fullWidth: boolean;
  maxWidth: number;
  maxWidthType: "%" | "px";
  width: number;
  height: number;
  marginType: "%" | "px";
  margin: number;
  paddingTop: number;
  paddingBottom: number;
}

export interface GeneralSettings {
  backgroundColor: string;
  offers: string;
  align: TextAlign;
}

export interface TextSettings {
  textColor: string;
  fontFamily: string;
  fontWeight: number;
  fontSize: string;
  textTransform: TextTransform;
}

export interface GetButtonSettings {
  backgroundColor: string;
  textColor: string;
  fontFamily: string;
  fontSize: string;
  fontWeight: number;
  rounding: number;
  borderColor?: string;
  textTransform?: TextTransform;
}

export interface BehaviourSettings {
  displayBy: "time" | "scroll";
  afterTime?: number;
}

export interface LocationSettings {
  location: LocationPosition;
  transition: number;
  x?: number;
  y?: number;
}

export interface ButtonLocationSettingsT {
  location: LocationPosition;
  x?: number;
  y?: number;
}

export interface ButtonSizesSettings {
  type?: "flex" | "fixed";
  w: number;
  h: number;
}

export interface ButtonStyleSettingsT {
  textColor: string;
  fontFamily: string;
  fontWeight: number;
  fontSize: string;
  textTransform: TextTransform;
  backgroundColor: string;
  borderTopLeftRadius?: number;
  borderTopRightRadius?: number;
  borderBottomLeftRadius?: number;
  borderBottomRightRadius?: number;
  circleSize?: number;
}

export interface ImageSettings {
  size: ImageSizes;
  rounding: number;
}

export interface AutoEmailStorage {
  offerLink: string;
  offerId: string;
  email: string;
  layoutId: string;
}

export interface SelectedOfferStorage {
  id: string;
  link: string;
  active: boolean;
  layoutId: string;
}

export interface ImageRepresentationSettings {
  position: ImageRepresentation;
}

export enum ImageSizes {
  small = "small",
  medium = "medium",
  large = "large",
}

export interface BoundingClient {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
  x: number;
  y: number;
}

export interface OffersIdSlots {
  id: string;
  slot: number;
}

export interface QueryPostVoucherCode {
  campaignId: string;
  campaignName: string;
  companyId: string;
  placementId: string;
  placementName: string;
  templateId: string;
  layoutId: string;
  offerId: string;
  orderId: string;
}

export enum TextTransform {
  uppercase = "uppercase",
  capitalize = "capitalize",
}

export enum ButtonShapes {
  roundingAll = "roundingAll",
  roundingTop = "roundingTop",
  roundingBottom = "roundingBottom",
  square = "square",
  circle = "circle",
  squareImage = "squareImage",
  squareImageLock = "squareImageLock",
}

export enum LocationPosition {
  bottom = "bottom",
  left = "left",
  center = "center",
  right = "right",
  top = "top",
  topLeft = "topLeft",
  topRight = "topRight",
  bottomLeft = "bottomLeft",
  bottomRight = "bottomRight",
}

export enum TextAlign {
  start = "start",
  left = "left",
  right = "right",
  center = "center",
  justify = "justify",
}

export interface ApiResponse {
  result: any;
  status: boolean;
}

export interface ApiErrorMessages {
  errorCode: number;
  message: string;
}

export enum LayoutClass {
  button = "button",
  action = "action",
  inline = "inline",
  popup = "popup",
}

export enum SinglePanels {
  overview = "overview",
  autoEmail = "autoEmail",
  faqs = "faqs",
  terms = "terms",
  share = "share",
}

export enum BundlePanels {
  faqs = "faqs",
  terms = "terms",
}

export const ICONS_DEFAULT_VALUES = {
  iconInfo: ` <svg
  width="15"
  height="15"
  viewBox="0 0 15 15"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M7.5 0C11.6421 0 15 3.35786 15 7.5C15 11.6421 11.6421 15 7.5 15C3.35786 15 0 11.6421 0 7.5C0 3.35786 3.35786 0 7.5 0ZM7.5 0.882353C3.84517 0.882353 0.882353 3.84517 0.882353 7.5C0.882353 11.1548 3.84517 14.1176 7.5 14.1176C11.1548 14.1176 14.1176 11.1548 14.1176 7.5C14.1176 3.84517 11.1548 0.882353 7.5 0.882353ZM8.02941 5.73529V11.9118H7.04118V5.73529H8.02941ZM7.53529 3.11647C7.94294 3.11647 8.28882 3.45 8.28882 3.85765C8.28882 4.26529 7.94294 4.61118 7.53529 4.61118C7.12765 4.61118 6.79412 4.26529 6.79412 3.85765C6.79412 3.45 7.12765 3.11647 7.53529 3.11647Z"
    fill="#888888"
  />
</svg>`,
  iconEmail: `<svg
width="14"
height="11"
viewBox="0 0 14 11"
fill="none"
xmlns="http://www.w3.org/2000/svg"
>
<path
  d="M13 2.125C13 1.50625 12.46 1 11.8 1H2.2C1.54 1 1 1.50625 1 2.125M13 2.125V8.875C13 9.49375 12.46 10 11.8 10H2.2C1.54 10 1 9.49375 1 8.875V2.125M13 2.125L7 6.0625L1 2.125"
  stroke="#888888"
  strokeWidth="1.5"
  strokeLinecap="round"
  strokeLinejoin="round"
/>
</svg>`,
  iconShare: ` <svg
    viewBox="64 64 896 896"
    focusable="false"
    data-icon="share-alt"
    width="1em"
    height="1em"
    fill="#888888"
    aria-hidden="true"
  >
    <path d="M752 664c-28.5 0-54.8 10-75.4 26.7L469.4 540.8a160.68 160.68 0 000-57.6l207.2-149.9C697.2 350 723.5 360 752 360c66.2 0 120-53.8 120-120s-53.8-120-120-120-120 53.8-120 120c0 11.6 1.6 22.7 4.7 33.3L439.9 415.8C410.7 377.1 364.3 352 312 352c-88.4 0-160 71.6-160 160s71.6 160 160 160c52.3 0 98.7-25.1 127.9-63.8l196.8 142.5c-3.1 10.6-4.7 21.8-4.7 33.3 0 66.2 53.8 120 120 120s120-53.8 120-120-53.8-120-120-120zm0-476c28.7 0 52 23.3 52 52s-23.3 52-52 52-52-23.3-52-52 23.3-52 52-52zM312 600c-48.5 0-88-39.5-88-88s39.5-88 88-88 88 39.5 88 88-39.5 88-88 88zm440 236c-28.7 0-52-23.3-52-52s23.3-52 52-52 52 23.3 52 52-23.3 52-52 52z"></path>
  </svg>`,
};

export const preFooterTextPost: string =
  "By selecting the option above you will be directed to a third party website. Any use of third party websites are not subject to our Privacy Policy, and we encourage you to also read their privacy statements";

export const preFooterTextPre: string =
  "By selecting you will receive by e-mail after purchase";
